import React, { useEffect, useRef } from 'react';

/**
 * Spatial depth stage for layered collages.
 * Tilts its children in 3D toward the pointer; children opt into depth with
 * `style={{ '--depth': '40px' }}` (see .depth-stage rules in LandingPage.css).
 *
 * Writes CSS custom properties from a rAF loop instead of React state, so the
 * pointer never triggers a re-render. Fine pointers only, and fully disabled
 * under prefers-reduced-motion.
 */
export function DepthStage({ children, className = '', maxTilt = 8, ...rest }) {
  const ref = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const canHover = window.matchMedia('(hover: hover) and (pointer: fine)');
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
    if (!canHover.matches || reduced.matches) return undefined;

    let frame = 0;
    let target = { x: 0, y: 0 };
    let current = { x: 0, y: 0 };

    // Critically damped follow: eases toward the pointer without overshoot,
    // and is interruptible at any frame because it always starts from `current`.
    const tick = () => {
      current.x += (target.x - current.x) * 0.14;
      current.y += (target.y - current.y) * 0.14;
      el.style.setProperty('--tilt-x', `${(-current.y * maxTilt).toFixed(3)}deg`);
      el.style.setProperty('--tilt-y', `${(current.x * maxTilt).toFixed(3)}deg`);
      el.style.setProperty('--glare-x', `${((current.x + 1) * 50).toFixed(1)}%`);
      el.style.setProperty('--glare-y', `${((current.y + 1) * 50).toFixed(1)}%`);
      if (Math.abs(target.x - current.x) > 0.001 || Math.abs(target.y - current.y) > 0.001) {
        frame = requestAnimationFrame(tick);
      } else {
        frame = 0;
      }
    };
    const kick = () => {
      if (!frame) frame = requestAnimationFrame(tick);
    };

    const onMove = (e) => {
      const r = el.getBoundingClientRect();
      target = {
        x: Math.max(-1, Math.min(1, ((e.clientX - r.left) / r.width) * 2 - 1)),
        y: Math.max(-1, Math.min(1, ((e.clientY - r.top) / r.height) * 2 - 1)),
      };
      el.classList.add('is-engaged');
      kick();
    };
    const onLeave = () => {
      target = { x: 0, y: 0 };
      el.classList.remove('is-engaged');
      kick();
    };

    el.addEventListener('pointermove', onMove);
    el.addEventListener('pointerleave', onLeave);
    return () => {
      cancelAnimationFrame(frame);
      el.removeEventListener('pointermove', onMove);
      el.removeEventListener('pointerleave', onLeave);
    };
  }, [maxTilt]);

  return (
    <div ref={ref} className={`depth-stage ${className}`} {...rest}>
      <div className="depth-stage-plane">{children}</div>
    </div>
  );
}

export default DepthStage;
